import React from 'react';
import { Button, Chip, Link, Sheet, Stack, Typography } from '@mui/joy';
import type { DocumentShareLinkRecord } from '../services/documentShareLinks.service';
import type { TransmissionArtefact } from '../utils/transmissionArtefact';

export function DocumentShareLinksList({
  links,
  loading = false,
  emptyLabel = 'No public transmissions yet.',
  openLinkLabel = 'Open transmission',
  nullifyingId = null,
  showVersionLabels = true,
  showArtefactLabels = false,
  artefactByLinkId = {},
  onNullify,
}: {
  links: DocumentShareLinkRecord[];
  loading?: boolean;
  emptyLabel?: string;
  openLinkLabel?: string;
  nullifyingId?: string | null;
  showVersionLabels?: boolean;
  showArtefactLabels?: boolean;
  artefactByLinkId?: Record<string, TransmissionArtefact>;
  onNullify?: (link: DocumentShareLinkRecord) => void;
}) {
  if (loading) {
    return <Typography level="body-sm" sx={{ color: 'text.secondary' }}>Loading links…</Typography>;
  }
  if (links.length === 0) {
    return (
      <Sheet variant="soft" sx={{ p: 1.25, borderRadius: 'md' }}>
        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>{emptyLabel}</Typography>
      </Sheet>
    );
  }
  return (
    <Stack spacing={1.1}>
      {links.map((link) => {
        const sentAtLabel = new Date(link.sentAt).toLocaleString('en-GB');
        const artefact = artefactByLinkId[link.id];
        return (
          <Sheet
            key={link.id}
            variant="outlined"
            sx={{
              p: 1.25,
              borderRadius: 'md',
              bgcolor: 'background.body',
              borderColor: link.revokedAt ? 'danger.200' : 'neutral.300',
              transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
              '&:hover': {
                borderColor: link.revokedAt ? 'danger.300' : 'primary.300',
                boxShadow: 'sm',
              },
            }}
          >
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'flex-start' }}
            >
              <Stack spacing={0.65}>
                <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
                  <Chip size="sm" variant="soft" color="neutral">Sent {sentAtLabel}</Chip>
                  {showArtefactLabels && artefact === 'first' && (
                    <Chip size="sm" variant="soft" color="primary">First Survey</Chip>
                  )}
                  {showArtefactLabels && artefact === 'final' && (
                    <Chip size="sm" variant="soft" color="success">Final Survey</Chip>
                  )}
                  {showArtefactLabels && artefact === 'unknown' && (
                    <Chip size="sm" variant="soft" color="neutral">Unclassified</Chip>
                  )}
                  {showVersionLabels && link.sentVersionNumber != null && (
                    <Chip size="sm" variant="soft" color="primary">Version {link.sentVersionNumber}</Chip>
                  )}
                  {link.isOutdated && !link.revokedAt && (
                    <Chip size="sm" color="warning" variant="solid">OUTDATED</Chip>
                  )}
                  {link.revokedAt && (
                    <Chip size="sm" color="danger" variant="solid">NULLIFIED</Chip>
                  )}
                </Stack>
                {link.url ? (
                  <Link
                    href={(() => {
                      try {
                        const u = new URL(link.url);
                        u.searchParams.set('snapshot', '1');
                        return u.toString();
                      } catch {
                        return `${link.url}${link.url.includes('?') ? '&' : '?'}snapshot=1`;
                      }
                    })()}
                    target="_blank"
                    rel="noopener noreferrer"
                    level="body-sm"
                    sx={{ fontWeight: 600 }}
                  >
                    {openLinkLabel}
                  </Link>
                ) : null}
                {link.revokedReason && (
                  <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                    Nullify reason: {link.revokedReason}
                  </Typography>
                )}
              </Stack>
              {!link.revokedAt && onNullify && (
                <Button
                  size="sm"
                  color="danger"
                  variant="outlined"
                  sx={{ minWidth: 110 }}
                  loading={nullifyingId === link.id}
                  onClick={() => onNullify(link)}
                >
                  NULLIFY
                </Button>
              )}
            </Stack>
          </Sheet>
        );
      })}
    </Stack>
  );
}
